
import { v4 as uuidv4, v4 } from 'uuid';
import { keyId, discountedPrice, positionNumber } from '../../lib/utils';

// A SERIES OF DOCS USED THROUGHOUT THE APP THAT ALLOWS TO CREATE NEW DOCS WITH THE CORRECT STRUCTURE

/**
 * Creates a new doc for a non conformity to be archived.
 * The doc will have the same fields as the original non conformity,
 * #deviceNonConformities
 * but with the additional fields:
 *      - _type: "deviceNonConformity"
 *      - status: the status of the non conformity
 *      - archivedBy: the user that archived the non conformity
 *      - archivedOn: the date the non conformity was archived
 *
 * @param {Object} nonConformity - the non conformity to be archived
 * @returns {Object} a new doc with the same fields as the original non conformity, plus the additional fields
 */
export const archiveNonConformityDoc = (nonConformity) =>( {
    _key: nonConformity?._key ? nonConformity?._key : uuidv4(),
    _id: nonConformity?._id ? nonConformity?._id : uuidv4(),
    nonConformity: nonConformity?.nonConformity,
    nNonConformities: nonConformity?.nNonConformities,
    image: nonConformity?.image,
    comments: nonConformity?.comments,
    createdOn: nonConformity?.createdOn,
    postedBy: nonConformity?.postedBy,
    assignedOn: nonConformity?.assignedOn,
    assigneBy: nonConformity?.assigneBy,
    completedBy: nonConformity?.completedBy,
    completedOn: nonConformity?.completedOn,
    _type:"deviceNonConformity",
    status: nonConformity?.status,
    archivedBy: nonConformity?.archivedBy, 
    archivedOn: nonConformity?.archivedOn,
})

/**
 * Creates a new doc for a device.
 * The doc will have the same fields as the original device,
 * plus the additional fields:
 *      - _type: "device"
 *      - status: the status of the device
 *      - nonConformities: the nonConformities related to the device
 *      - archivedBy: the user that archived the device
 *      - archivedOn: the date the device was archived
 *#devices
 * @param {Object} device - the device to be archived
 * @returns {Object} a new doc with the same fields as the original device, plus the additional fields
 */
export const deviceDoc = (device) => {
    return(
       {
            _key: nonConformity?._key ? nonConformity?._key : uuidv4(),
            _id: nonConformity?._id ? nonConformity?._id : uuidv4(),
           image: device?.image,
           label: device?.label,
           locations: device?.locations,
           templateDevice: device?.templateDevice,
           substituteDevice: device?.substituteDevice,
           substituteFields: device?.substituteFields,
           substituteReason: device?.substituteReason,
           fields: device?.fields,
           dates: device?.dates,
           userLog: device?.userLog,
           postedBy: device?.postedBy,
           comments: device?.comments,
           postedBy: device?.postedBy,
           createdOn: device?.createdOn,
           archivedBy: device?.archivedBy,
           archivedOn: device?.archivedOn,
           status: device?.status,
           nonConformities: device?.nonConformities,
           _type:"device", 
       }
    )
   }

/**
 * Creates a new doc for a user log.
 * #historyLogs
 * The doc will have the following fields:
 *      - _type: "userLog"
 *      - _key: a unique key for the doc
 *      - _id: a unique id for the doc
 *      - logType: the type of the log
 *      - scannedWithQr: whether the log was scanned with QR
 *      - date: the date the log was created
 *      - postedBy: the user that created the log
 *      - latitude: the latitude of the user when the log was created
 *      - longitude: the longitude of the user when the log was created
 *      - historyDevice: the device related to the log
 *
 * @param {Object} user - the user that created the log
 * @param {String} logType - the type of the log
 * @param {Object} historyDevice - the device related to the log
 * @param {Object} position - the position of the user when the log was created
 * @param {Boolean} scannedWithQr - whether the log was scanned with QR
 * @returns {Object} a new doc with the fields above
 */
   export const logDoc = (user, logType, historyDevice, position, scannedWithQr) => ({ 
    _type: "userLog",
    _key: keyId(), 
    _id: uuidv4(),     
    logType,
    scannedWithQr,
    date: Date.now(),
    postedBy:{
        _ref: user?._id,
        _type: "postedBy",
        _key: keyId()
    },
    latitude: position?.coords?.latitude,
    longitude: position?.coords?.longitude,
    historyDevice
        
})


/**
 * Creates a new doc for a device non-conformity.
 * #deviceNonConformities
 * The doc will have the following fields:
 *      - _type: "deviceNonConformity"
 *      - _key: a unique key for the doc
 *      - _id: a unique id for the doc
 *      - postedBy: the user that created the doc
 *      - createdOn: the date the doc was created
 *      - status: the status of the doc
 *      - comments: an array of comments related to the doc
 *      - nNonConformities: the number of non-conformities related to the doc
 *      - image: an array of images related to the doc
 *      - nonConformity: the non-conformity related to the doc
 *
 * @param {String} nonConformityId - the id of the non-conformity
 * @param {Object} user - the user that created the doc
 * @returns {Object} a new doc with the fields above
 */
export const deviceNonConformityDoc = (nonConformityId, user) => {
    const now = Date.now()
   return ({
        _id: uuidv4(),
        postedBy: user?._id ? [{ _type: 'postedBy', _ref: user?._id,  _key: keyId(), }]:[], 
        createdOn: now,
        status:"DA FARE",
        comments:[],
        nNonConformities:"1",
        _key: keyId(),
        _type: "deviceNonConformity",
        image:[],
        nonConformity:{
            _ref: nonConformityId,
            _type: "reference",
            _key: keyId(),
        }
    })
}

/**
 * Creates a new doc for a RichTextObject.
 * #richText
 * The doc will have the following fields:
 *      - blocks: an array of text blocks
 *      - entityMap: an object that maps entity keys to entity values
 *      - _key: a unique key for the doc
 *      - _id: a unique id for the doc
 *      - _type: "RTObject"
 *
 * @param {String} text - the text of the RichTextObject
 * @returns {Object} a new doc with the fields above
 */
export const richEditorDoc = (text="") => {
    //const key = keyId()
    const doc = {
        blocks:[
            {
            data:{_key: keyId()},
            depth: 0,
            entityRanges: [],
            inlineStyleRanges : [],
            key: keyId(),
            _key: keyId(),
            text: text,
            type:"unstyled"
            }

        ],
        entityMap: {_key: keyId()},
        _key: keyId(),
        _id: uuidv4(),
        _type:"richTextObject"
    }
    return doc
}
/**
 * #richText
 * @param {*} blocks array of type richEditorListBlockDoc
 * @returns 
 */
export const richEditorDocAddBlocks = (blocks=[]) => {
    //const key = keyId()
    const doc = {
        blocks:[...blocks],
        entityMap: {_key: keyId()},
        _key: keyId(),
        _id: uuidv4(),
        _type:"richTextObject"
    }
    return doc
}
/**
 * Returns a rich editor document block for a unordered list item.
 * #richText
 * @param {string} text - the text for the block
 * @returns {object} - a rich editor document block
 */
export const richEditorListBlockDoc = (text="") => {
    //const key = keyId()
    const doc = 
            {
            data:{_key: keyId()},
            depth: 0,
            entityRanges: [],
            inlineStyleRanges : [],
            key: keyId(),
            _key: keyId(),
            text: text,
            type:"unordered-list-item"
            }
    return doc
}



/**
 * Creates an offer doc from customer product, product and price list.
 * #offers
 * The doc will have the following fields:
 *      - customerProductId: the id of the customer product
 *      - label: the label of the customer product
 *      - nonConformity: the non-conformity of the customer product
 *      - hideNonConformityDescription: whether to hide the non-conformity description of the customer product
 *      - customerProduct: the label of the customer product
 *      - pricelistCode: the price list code of the price list
 *      - law: the law of the customer product
 *      - lawLabel: the label of the law of the customer product
 *      - description: the description of the customer product
 *      - descriptionText: the text of the description of the customer product
 *      - unitPrice: the unit price of the price list
 *      - supplierPrice: the supplier price of the product
 *      - supplierProductId: the id of the product
 *      - isSign: whether the customer product is a sign
 *      - allowZero: whether the customer product allows zero quantity
 *      - labourHours: the labour hours of the customer product
 *      - unit: the unit of the customer product
 *
 * @param {Object} customerProduct - the customer product
 * @param {Object} product - the product
 * @param {Object} priceList - the price list
 * @returns {Object} - an offer doc
 */
export const offerDocFromProductsAndList = (customerProduct, product, priceList, client) => ({
 
    customerProductId: customerProduct?._id?.toString(),
    label: customerProduct?.label?.toString(), 
    nonConformity: customerProduct?.nonConformity?.toString(), 
    hideNonConformityDescription: customerProduct?.hideNonConformityDescription?.toString(), 
    addFormulario: customerProduct?.addFormulario,
    customerProduct: customerProduct?.label?.toString(), 
    pricelistCode: priceList?.priceListSource?.toString(), 
    law: customerProduct?.law?.richText,
    lawLabel: customerProduct?.law?.label,
    description: customerProduct?.details,
    descriptionText: customerProduct?.details?.blocks?.map?.((x)=>x?.text)?.toString(),
    unitPrice: priceList?.price?.toString(),
    supplierPrice: product?.price?.toString(),
    supplierProductId: product?._id,
    isSign: customerProduct?.isSign, 
    allowZero: customerProduct?.allowZero, 
    labourHours: customerProduct?.labourHours?.toString(),
    unit: customerProduct?.unit?.toString(),
    //manualQuantity: customerProduct?.manualQuantity?.toString(),
})
/**
 * Creates an offer doc from a customer product.
 * #offers
 * #customerProducts
 * The doc will have the following fields:
 *      - customerProductId: the id of the customer product
 *      - label: the label of the customer product
 *      - nonConformity: the non-conformity of the customer product
 *      - hideNonConformityDescription: whether to hide the non-conformity description of the customer product
 *      - customerProduct: the label of the customer product
 *      - law: the law of the customer product
 *      - lawLabel: the label of the law of the customer product
 *      - description: the description of the customer product
 *      - descriptionText: the text of the description of the customer product
 *      - isSign: whether the customer product is a sign
 *      - allowZero: whether the customer product allows zero quantity
 *      - labourHours: the labour hours of the customer product
 *      - unit: the unit of the customer product
 * @param {Object} customerProduct - the customer product
 * @returns {Object} - an offer doc
 */
export const offerDocFromProduct = (customerProduct) => ({
 
    customerProductId: customerProduct?._id?.toString(),
    label: customerProduct?.label?.toString(), 
    nonConformity: customerProduct?.nonConformity?.toString(), 
    hideNonConformityDescription: customerProduct?.hideNonConformityDescription?.toString(),
     addFormulario: customerProduct?.addFormulario, 
    customerProduct: customerProduct?.label?.toString(), 
    law: customerProduct?.law?.richText,
    lawLabel: customerProduct?.law?.label,
    description: customerProduct?.details,
    descriptionText: customerProduct?.details?.blocks?.map?.((x)=>x?.text)?.toString(),
    isSign: customerProduct?.isSign, 
    allowZero: customerProduct?.allowZero, 
    labourHours: customerProduct?.labourHours?.toString(),
    unit: customerProduct?.unit?.toString(),
    //manualQuantity: customerProduct?.manualQuantity?.toString(),
})

/**
 * Returns a new offer doc with the descriptionText field
 * #offers
 * @param {Object} item - the offer doc
 * @returns {Object} - a new offer doc with the descriptionText field
 */
export const offerDocWithDescriptionText = (item) => {
    
    const allText = item?.description?.blocks?.map?.((x)=>x?.text).toString()
    const doc = {
        ...item,
        descriptionText: allText
    }
    return doc
}

/**
 * Generates an offer doc with totals.
 * #offers
 * #generatedOffer
 * The doc will have the following fields:
 *      - _id: the id of the offer doc
 *      - _key: the key of the offer doc
 *      - registerId: the id of the register
 *      - siteId: the id of the site
 *      - nonConformity: the non-conformity of the customer product
 *      - hide: whether to hide the customer product
 *      - nonConformityId: the id of the non-conformity
 *      - registerTemplateId: the id of the register template
 *      - customerProduct: the label of the customer product
 *      - customerProductId: the id of the customer product
 *      - pricelistCode: the price list code of the price list
 *      - law: the law of the customer product
 *      - lawLabel: the label of the law of the customer product
 *      - addedLocation: the added location of the customer product
 *      - clientOrder: the client order of the customer product
 *      - edited: whether the customer product has been edited
 *      - description: the description of the customer product
 *      - descriptionText: the text of the description of the customer product
 *      - hideNonConformityDescription: whether to hide the non-conformity description of the customer product
 *      - location: the location of the customer product
 *      - isManual: whether the customer product is manual
 *      - quantity: the quantity of the customer product
 *      - unitPrice: the unit price of the customer product
 *      - discountedUnitPrice: the discounted unit price of the customer product
 *      - priceToClient: the price to the client of the customer product
 *      - discount: the discount of the customer product
 *      - supplierPrice: the supplier price of the customer product
 *      - supplierProductId: the id of the supplier product
 *      - totalCost: the total cost of the customer product
 *      - margin: the margin of the customer product
 *      - profit: the profit of the customer product
 *      - unitProfit: the unit profit of the customer product
 *      - order: the order of the customer product
 *      - offerPropertyId: the id of the offer property
 *      - offerPropertyLabel: the label of the offer property
 *      - deviceFamilyId: the id of the device family
 *      - deviceId: the id of the device
 *      - deviceNonConformityId: the id of the device non-conformity
 *      - allowZero: whether the customer product allows zero quantity
 *      - isSign: whether the customer product is a sign
 *      - labourHours: the labour hours of the customer product
 *      - labourProduct: the labour product of the customer product
 *      - unit: the unit of the customer product
 * @param {Object} item - the offer doc
 * @returns {Object} - a new offer doc with the descriptionText field
 */
export const generatedOfferWithTotalsDoc = (item={}) => {
    const {_id, _key, isManual, order, registerId,siteId, addFormulario, isSign, hide, allowZero, addedLocation, clientOrder, edited, nonConformity, deviceId, nonConformityId, registerTemplateId, customerProduct, customerProductId, pricelistCode, law, lawLabel, description, hideNonConformityDescription, location, quantity, unitPrice, supplierPrice, supplierProductId, discount, deviceFamilyId, offerPropertyId, offerPropertyLabel, offerProperty, deviceNonConformityId, labourHours, labourProduct, unit} = item
    //const discountedPrice = (price, discount) => price - (price * discount / 100 ).toFixed(2)
    const totalPrice = () => {
        if(unit) return Number(unitPrice) * 1?.toFixed(2).toString()
        if(labourProduct) return Number(unitPrice) * 1?.toFixed(2).toString()
        //if(manualQuantity) return (+unitPrice * +manualQuantity)?.toFixed(2).toString()
        return (Number(unitPrice) * Number(quantity))?.toFixed(2).toString()
    }
    const totalCost = () => {
        const labourHoursFormatted = labourHours ? Number(labourHours) : 1
        if(labourProduct) return  ((+supplierPrice * labourHoursFormatted)?.toFixed(2))
        if(unit) return supplierPrice && +supplierPrice*1?.toFixed(2).toString()
       // if(manualQuantity) return (+supplierPrice * +manualQuantity)?.toFixed(2).toString()
        return (+supplierPrice * Number(quantity))?.toFixed(2).toString()
    }
    const priceToClient = discountedPrice(Number(totalPrice()), Number(discount))?.toFixed(2)?.toString()

    const profit = (Number(priceToClient) - Number(totalCost())).toFixed(2)?.toString()
     //#margin
    const margin = ((Number(profit)/Number(priceToClient)) * 100).toFixed(2)?.toString()
    //const margin = ((Number(profit)/Number(totalCost())) * 100)?.toFixed(2)
    //const margin = 100
    
    //if(profit == "23.46") console.log("margin qòwodkpqowkd", {margin, profit, priceToClient, totalCost: totalCost()})
    //console.log("margin qwòlskqòws,", {margin, profit, totalCost: totalCost()})

    const unitProfit = (discountedPrice(+unitPrice, +discount) - +supplierPrice).toFixed(2)?.toString()
    const discountedUnitPrice = discountedPrice(Number(unitPrice), Number(discount)).toFixed(2)?.toString()

    const doc = {
        _id,
        _key,
        registerId,
        siteId,
        nonConformity,
        hide,
        nonConformityId,
        registerTemplateId,
        customerProduct,
        customerProductId,
        pricelistCode,
        law,
        lawLabel,
        addedLocation, 
        clientOrder,
        edited,
        description,
        addFormulario,
        descriptionText: description?.blocks?.map?.((x)=>x?.text)?.toString(),
        hideNonConformityDescription,
        location,
        isManual,
        quantity: quantity*1.00?.toFixed(2).toString(),
        unitPrice: +unitPrice*1.00?.toFixed(2).toString(),
        discountedUnitPrice: discountedUnitPrice * 1.00?.toFixed(2).toString(),
        priceToClient:+priceToClient*1.00?.toFixed(2).toString(),
        discount:+discount*1.00?.toFixed(2).toString(),
        supplierPrice: +supplierPrice*1.00?.toFixed(2).toString(),
        supplierProductId: supplierProductId,
        totalCost: +totalCost()*1.00?.toFixed(2).toString(),
        margin: !Number(margin) ? 0 : +margin*1.00?.toFixed(2).toString(),
        profit:+profit*1.00?.toFixed(2).toString(),
        unitProfit:+unitProfit*1.00?.toFixed(2).toString(),
        order,
        offerPropertyId,
        offerPropertyLabel,
        deviceFamilyId,
        deviceId,
        deviceNonConformityId, 
        allowZero,
        isSign,
        labourHours,
        labourProduct,

        unit
    }
    //if(isManual)console.log("ALL REG OFFER", doc)
                    
    return doc
}

/**
 * Generated a new offer document based on the given offerField.
 * #offers
 * @param {object} offerField - The offerField object to generate the new offer document from.
 * @returns {object} - The new offer document.
 */
export const generatedOfferDoc = (offerField) => ({
    _id: offerField?._id ? offerField?._id : uuidv4(),
    customerProductId: offerField?.customerProductId?.toString(),
    registerId: offerField?.registerId?.toString(),
    siteId: offerField?.siteId?.toString(),
    nonConformity: offerField?.nonConformity?.toString(), 
    hideNonConformityDescription: offerField?.hideNonConformityDescription?.toString(), 
    offerPropertyId: offerField?.offerPropertyId?.toString(), 
    offerPropertyLabel: offerField?.offerPropertyLabel?.toString(), 
    nonConformityId: offerField?.nonConformityId?.toString(),
    registerTemplateId: offerField?.registerTemplateId?.toString(),
    customerProduct: offerField?.customerProduct?.toString(), 
    pricelistCode: offerField?.pricelistCode?.toString(), 
    law: offerField?.law,
    lawLabel: offerField?.lawLabel,
    addedLocation: offerField?.addedLocation, 
    clientOrder: offerField?.clientOrder,
    edited: offerField?.edited,
    deviceId: offerField?.deviceId?.toString(),
    description: offerField?.description,
    descriptionText: offerField?.description?.blocks?.map?.((x)=>x?.text)?.toString(),
    location: offerField?.location?.toString(), 
    quantity: offerField?.quantity?.toString(), 
    unitPrice: offerField?.unitPrice?.toString(),
    supplierPrice: offerField?.supplierPrice?.toString(),
    supplierProductId: offerField?.supplierProductId?.toString(),
    discount: offerField?.discount?.toString(),
    deviceFamilyId: offerField?.deviceFamilyId?.toString(), 
    deviceNonConformityId: offerField?.deviceNonConformityId?.toString(),
    order: offerField?.order?.toString(),
    _key: offerField?._key ? offerField?._key : keyId(),
    isSign: offerField?.isSign, 
    allowZero: offerField?.allowZero, 
    labourHours: offerField?.labourHours?.toString(),
    labourProduct: offerField?.labourProduct,
    hide: offerField?.hide,
    unit: offerField?.unit?.toString(),
    //manualQuantity: offerField?.manualQuantity?.toString(),
    isManual:offerField?.isManual,

})



/**
 * Returns a reference document object based on the given id and key.
 * If id is undefined, returns undefined.
 * #core
 * If key is undefined, uses the keyId() function to generate a key.
 * @param {string} id - The id of the document to reference.
 * @param {string} key - The key of the document to reference.
 * @returns {object} - The reference document object.
 */
export const referenceDoc = (id, key) => {
    if(id) return {_ref: id, _type:"reference", _key: key ? key : keyId()}
    return undefined
  }


/**
 * Generates an expanded device document object based on the given device and deviceNonConformity.
 * The expanded document object has the following fields:
 *      - positionNumber: the position number of the device
 *      - templateDeviceId: the id of the template device
 *      - nNonConformities: the number of non-conformities of the device
 * The number of non-conformities is calculated as follows: if the deviceNonConformity is undefined or its nNonConformities field is undefined, returns 1. Otherwise, returns the value of nNonConformities.
 * @param {object} device - The device object to expand.
 * @param {object} deviceNonConformity - The deviceNonConformity object to expand the device object with.
 * @returns {object} - The expanded device document object.
 */
 
  export const deviceExpandedDoc = (device, deviceNonConformity) => {
    const quantity = () => {
        if(!deviceNonConformity?.nNonConformities) return 1
        return + deviceNonConformity?.nNonConformities
    }
    const doc = {
        ...device, 
        positionNumber: positionNumber(device), 
        templateDeviceId: device?.templateDevice?._ref,
        nNonConformities: quantity()

     }
     return doc
}